import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { StorageService } from '@shared/core/services/storage.service';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { cityIn } from '@shared/utils/declination-cities';
import { MetaService } from '@shared/services/meta/meta.service';
import { BreadcrumbItem } from '@shared/modules/breadcrumb/types/breadcrumbItem';
import {
  PromotionClient,
  PromotionImageResponse,
  PromotionResponse,
} from '@medindex-webapi';
import { RegionsService } from '@shared/services/regions.service';
import { NavigationMenuService } from '@shared/services/navigation-menu.service';

@Component({
  selector: 'app-promotions',
  templateUrl: './promotions.component.html',
  styleUrls: ['./promotions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RegionsService],
})
export class PromotionsComponent implements OnInit, OnDestroy {
  /** Заголовок с городом. */
  title$ = this.regions.getTitleWithCity('Скидки и акции');

  /** Навигация с городом. */
  breadcrumbs$ = this.title$.pipe(
    map((title) => {
      return [
        { href: '/', text: 'Главная' },
        { href: '', text: title },
      ] as BreadcrumbItem[];
    })
  );

  public dataLoaded$ = new BehaviorSubject(false);

  protected promotionsList$ = new BehaviorSubject<PromotionResponse[]>([]);
  protected sliders$ = new BehaviorSubject<PromotionImageResponse[]>([]);
  private readonly ROTATED_PROMOTIONS_COUNT = 3;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    protected navigationMenu: NavigationMenuService,
    private promotionClient: PromotionClient,
    private metaService: MetaService,
    private storage: StorageService,
    private regions: RegionsService
  ) {}

  ngOnInit(): void {
    this.getPromotionsList();
    this.setMeta();
  }

  private setMeta(): void {
    this.storage.cityName$
      .pipe(
        filter((city): city is string => !!city),
        tap((city: string) => {
          const title = `Скидки и акции в ${cityIn(
            city
          )} на анализы в лабораторной службе Хеликс`;
          const description = `Актуальные скидки и акции на анализы в лабораторной службе Хеликс в ${cityIn(
            city
          )}. ✅ Записывайтесь онлайн!`;

          this.metaService.title(title);
          this.metaService.description(description);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private getPromotionsList(): void {
    this.storage.cityId$
      .pipe(
        filter((cityId): cityId is number => !!cityId),
        this.regions.getWaitForAliasAppliedToUrlPipeOperator(),
        switchMap((cityId) =>
          combineLatest([
            this.promotionClient.getListByCity(cityId, undefined, undefined),
            this.promotionClient.getImages(cityId),
          ])
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([promotions, sliders]) => {
        const promotionList: PromotionResponse[] = this.checkRotatedPromotions(
          promotions.promotions ?? []
        );

        this.promotionsList$.next(promotionList);
        this.sliders$.next(sliders.filter((slide) => !!slide.link));
        this.dataLoaded$.next(true);
      });
  }

  private checkRotatedPromotions(
    promotionsList: PromotionResponse[]
  ): PromotionResponse[] {
    const promotions: PromotionResponse[] = [...promotionsList];

    const countRotatedPromotions: number = promotions.filter(
      (promotion: PromotionResponse) => promotion.isRotated
    ).length;

    if (countRotatedPromotions < this.ROTATED_PROMOTIONS_COUNT) {
      promotions.map(
        (promotion: PromotionResponse): boolean => (promotion.isRotated = false)
      );
    }

    return promotions;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
